import { useEffect, useRef } from "react";
import { decodePolyline, getPaceColor, MAPBOX_TOKEN } from "./utils";

export default function RunMap({
  run,
  raw,
  hrData,
  velocityData,
  latlng,
  distanceData,
  zoneDist,
  hoverIndex,
  walkLatlng,
  hasPostRun,
}) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const mapLoadedRef = useRef(false);

  // Init map
  useEffect(() => {
    if (!run || mapLoadedRef.current) return;

    const polyline = run.summary_polyline || raw.map?.summary_polyline;
    const coords = decodePolyline(polyline);
    if (!coords.length) return;

    const useStreams = latlng.length > 0;
    const routeCoords = useStreams ? latlng.map(([lat, lng]) => [lng, lat]) : coords;

    function initMap() {
      if (mapInstanceRef.current || !mapRef.current || !window.mapboxgl) return;

      window.mapboxgl.accessToken = MAPBOX_TOKEN;
      const map = new window.mapboxgl.Map({
        container: mapRef.current,
        style: "mapbox://styles/mapbox/light-v11",
        bounds: routeCoords.reduce(
          (b, c) => [
            [Math.min(b[0][0], c[0]), Math.min(b[0][1], c[1])],
            [Math.max(b[1][0], c[0]), Math.max(b[1][1], c[1])],
          ],
          [
            [routeCoords[0][0], routeCoords[0][1]],
            [routeCoords[0][0], routeCoords[0][1]],
          ]
        ),
        fitBoundsOptions: { padding: 48 },
        attributionControl: false,
      });

      map.on("load", () => {
        // Draw route with pace-colored gradient using a single GeoJSON source
        // Much more efficient than one source per segment, and renders like NRC
        if (useStreams && velocityData.length > 0 && routeCoords.length > 1) {
          const validV = velocityData.filter(Boolean);
          const minV = Math.min(...validV);
          const maxV = Math.max(...validV);

          // Build a GeoJSON with per-point color properties using line-gradient
          // Mapbox line-gradient needs colors as stops from 0→1 along the line
          const stops = [];
          const len = Math.min(routeCoords.length, velocityData.length);
          for (let i = 0; i < len; i++) {
            const pct = i / (len - 1);
            stops.push(pct, getPaceColor(velocityData[i], minV, maxV));
          }

          map.addSource("route", {
            type: "geojson",
            lineMetrics: true,
            data: {
              type: "Feature",
              geometry: { type: "LineString", coordinates: routeCoords.slice(0, len) },
            },
          });
          map.addLayer({
            id: "route",
            type: "line",
            source: "route",
            layout: { "line-join": "round", "line-cap": "round" },
            paint: {
              "line-width": 6,
              "line-gradient": ["interpolate", ["linear"], ["line-progress"], ...stops],
            },
          });
        } else {
          map.addSource("route", {
            type: "geojson",
            data: { type: "Feature", geometry: { type: "LineString", coordinates: routeCoords } },
          });
          map.addLayer({
            id: "route",
            type: "line",
            source: "route",
            layout: { "line-join": "round", "line-cap": "round" },
            paint: { "line-color": "#E8650A", "line-width": 5 },
          });
        }

        // Walk overlay — dashed grey line on top of run route
        if (hasPostRun && walkLatlng && walkLatlng.length > 1) {
          const walkCoords = walkLatlng.map(([lat, lng]) => [lng, lat]);
          map.addSource("walk", {
            type: "geojson",
            data: { type: "Feature", geometry: { type: "LineString", coordinates: walkCoords } },
          });
          map.addLayer({
            id: "walk",
            type: "line",
            source: "walk",
            layout: { "line-join": "round", "line-cap": "round" },
            paint: {
              "line-color": "#888888",
              "line-width": 4,
              "line-opacity": 0.65,
              "line-dasharray": [1.5, 2.5],
            },
          });
        }

        // Start dot
        const startEl = document.createElement("div");
        startEl.style.cssText =
          "width:14px;height:14px;background:#27AE60;border:3px solid #fff;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.3);";
        new window.mapboxgl.Marker({ element: startEl }).setLngLat(routeCoords[0]).addTo(map);

        // End dot
        const endEl = document.createElement("div");
        endEl.style.cssText =
          "width:14px;height:14px;background:#C0392B;border:3px solid #fff;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.3);";
        new window.mapboxgl.Marker({ element: endEl })
          .setLngLat(routeCoords[routeCoords.length - 1])
          .addTo(map);

        // Hover marker + distance label
        const dot = document.createElement("div");
        dot.style.cssText =
          "width:16px;height:16px;background:#fff;border:3px solid #1A1A2E;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.4);position:relative;display:none;";

        const label = document.createElement("div");
        label.style.cssText =
          "position:absolute;bottom:20px;left:50%;transform:translateX(-50%);background:#1A1A2E;color:#fff;padding:3px 8px;border-radius:6px;font-size:11px;white-space:nowrap;font-family:monospace;";

        dot.appendChild(label);

        markerRef.current = new window.mapboxgl.Marker({ element: dot, anchor: "center" })
          .setLngLat(routeCoords[0])
          .addTo(map);
        markerRef.current._hoverEl = dot;
        markerRef.current._label = label;
        markerRef.current._coords = routeCoords;
      });

      mapInstanceRef.current = map;
      mapLoadedRef.current = true;
    }

    // Load mapbox if not already loaded
    if (window.mapboxgl) {
      initMap();
    } else {
      const script = document.createElement("script");
      script.src = "https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js";
      script.onload = () => {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = "https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css";
        document.head.appendChild(link);
        setTimeout(initMap, 50);
      };
      document.head.appendChild(script);
    }
  }, [run]);

  // Update hover marker position + distance label
  useEffect(() => {
    if (!markerRef.current) return;
    const { _hoverEl, _label, _coords } = markerRef.current;

    if (hoverIndex === null || !_coords?.[hoverIndex]) {
      if (_hoverEl) _hoverEl.style.display = "none";
      return;
    }
    _hoverEl.style.display = "block"; // not "flex", not wrapper
    markerRef.current.setLngLat(_coords[hoverIndex]);

    const dist = distanceData?.[hoverIndex];
    if (dist !== undefined && _label) {
      _label.textContent = `${(dist / 1000).toFixed(2)} km`;
    }
  }, [hoverIndex, distanceData]);

  const hasMap = !!(run?.summary_polyline || raw?.map?.summary_polyline);

  return (
    <div style={{ position: "relative", height: 340, background: "#E8E8E8" }}>
      {hasMap ? (
        <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
      ) : (
        <div
          style={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#aaa",
            fontSize: 13,
          }}
        >
          No GPS route available
        </div>
      )}

      {/* Pace legend */}
      {velocityData.length > 0 && (
        <div
          style={{
            position: "absolute",
            bottom: 10,
            left: 10,
            display: "flex",
            gap: 6,
            alignItems: "center",
            background: "rgba(255,255,255,0.88)",
            borderRadius: 8,
            padding: "4px 8px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
          }}
        >
          <div
            style={{
              width: 40,
              height: 5,
              borderRadius: 99,
              background: "linear-gradient(to right, #3498DB, #27AE60, #F39C12, #E74C3C)",
            }}
          />
          <span style={{ fontSize: 10, color: "#555" }}>slow → fast</span>
        </div>
      )}
    </div>
  );
}
